import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Autocomplete,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FileDownload as FileDownloadIcon,
  GridOn as GridOnIcon,
  Visibility as VisibilityIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';

import MUIDataTable from "mui-datatables";
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import barcodeApi from '../services/barcodeApi';
import { useAuth } from '../contexts/AuthContext';

/** Сообщения модуля, означающие, что у товара просто нет партий */
const NO_BATCHES_MESSAGES = ['уже удалены', 'нет сгенерированных кодов'];

const AdminBarcodesPage = () => {
  const { canEdit } = useAuth();

  /** Справочник товаров */
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  /** Выбранный товар и его партии */
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);

  /** Диалог создания партии */
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createProduct, setCreateProduct] = useState(null);
  const [createCount, setCreateCount] = useState('');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  /** Диалог удаления партии */
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  /** Экспорт партии ('csv' | 'xlsx' | null) */
  const [exporting, setExporting] = useState(null);
  const [exportMenu, setExportMenu] = useState({ anchor: null, batchRow: null });

  /** Просмотр кодов партии (серверная пагинация) */
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewBatch, setViewBatch] = useState(null);
  const [viewCodes, setViewCodes] = useState([]);
  const [viewTotal, setViewTotal] = useState(0);
  const [viewPage, setViewPage] = useState(0);
  const [viewRowsPerPage, setViewRowsPerPage] = useState(50);
  const [viewLoading, setViewLoading] = useState(false);

  /** Проверка кода (тестовая, без отметки в базе) */
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  /** Уведомления */
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const pollTimer = useRef(null);
  const stopPolling = () => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  };
  useEffect(() => () => stopPolling(), []);

  /**
   * Загрузка списка товаров
   * @returns {Promise<void>}
   */
  const fetchProducts = useCallback(async () => {
    try {
      setProductsLoading(true);
      setProducts(await barcodeApi.getProducts());
    } catch (error) {
      setSnackbar({ open: true, message: `Не удалось загрузить товары: ${error.message}`, severity: 'error' });
    } finally {
      setProductsLoading(false);
    }
  }, []);

  /**
   * Загрузка партий товара
   * @param {Object} product - выбранный товар
   * @returns {Promise<void>}
   */
  const fetchBatches = useCallback(async (product) => {
    if (!product) {
      setBatches([]);
      return;
    }
    try {
      setLoading(true);
      setBatches(await barcodeApi.getBatches(product.id));
    } catch (error) {
      if (NO_BATCHES_MESSAGES.some((text) => error.message.includes(text))) {
        setBatches([]);
      } else {
        setSnackbar({ open: true, message: `Не удалось загрузить партии: ${error.message}`, severity: 'error' });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    fetchBatches(product);
  };

  /** --- Создание партии --- */

  const handleOpenCreateDialog = () => {
    setCreateProduct(selectedProduct);
    setCreateCount('');
    setGenerating(false);
    setProgress({ done: 0, total: 0 });
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    if (generating) {
      /** Генерация идёт асинхронно на сервере - просто прекращаем следить */
      stopPolling();
      setSnackbar({ open: true, message: 'Генерация продолжится на сервере. Обновите список партий позже.', severity: 'info' });
      if (selectedProduct) fetchBatches(selectedProduct);
    }
    setCreateDialogOpen(false);
    setGenerating(false);
  };

  const finishGeneration = (productId, total) => {
    setGenerating(false);
    setCreateDialogOpen(false);
    setSnackbar({ open: true, message: `Партия создана: ${total} кодов`, severity: 'success' });
    const product = products.find((item) => Number(item.id) === Number(productId));
    if (product) {
      setSelectedProduct(product);
      fetchBatches(product);
    }
  };

  const handleGenerate = async () => {
    const count = parseInt(createCount, 10);
    if (!createProduct || !count || count < 1) return;

    setGenerating(true);
    setProgress({ done: 0, total: count });

    try {
      const result = await barcodeApi.generateBatch(String(createProduct.product_code), count);
      const total = result.count;
      setProgress({ done: 0, total });

      /** Вставка кодов идёт асинхронно - опрашиваем прогресс */
      pollTimer.current = setInterval(async () => {
        try {
          const done = await barcodeApi.getGenerateProgress(result.product, result.batch);
          setProgress({ done, total });
          if (done >= total) {
            stopPolling();
            finishGeneration(result.product, total);
          }
        } catch (error) {
          /** Партия ещё не появилась в базе - ждём следующий тик */
        }
      }, 2000);
    } catch (error) {
      setGenerating(false);
      setSnackbar({ open: true, message: `Ошибка генерации: ${error.message}`, severity: 'error' });
    }
  };

  /** --- Удаление партии --- */

  const handleOpenDeleteDialog = (batchRow) => {
    setBatchToDelete(batchRow);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!batchToDelete || !selectedProduct) return;
    try {
      setDeleting(true);
      const count = await barcodeApi.deleteBatch(selectedProduct.id, batchToDelete.batch);
      setSnackbar({ open: true, message: `Партия №${batchToDelete.batch} удалена (${count} кодов)`, severity: 'success' });
      setDeleteDialogOpen(false);
      setBatchToDelete(null);
      fetchBatches(selectedProduct);
    } catch (error) {
      setSnackbar({ open: true, message: `Ошибка удаления: ${error.message}`, severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  /** --- Экспорт партии --- */

  /**
   * Коды партии отдаются с пагинацией - выгружаем всё по страницам
   * @param {Object} batchRow - строка партии из таблицы
   * @returns {Promise<Array<Object>>} - все коды партии
   */
  const fetchAllBarcodes = async (batchRow) => {
    const limit = 10000;
    const codes = [];
    let page = 1;
    let total = Infinity;

    while (codes.length < total) {
      const payload = await barcodeApi.getBatchBarcodes(selectedProduct.id, batchRow.batch, page, limit);
      const items = payload.barcodes?.data || [];
      total = payload.count || items.length;
      if (!items.length) break;
      codes.push(...items);
      setSnackbar({ open: true, message: `Загрузка кодов: ${codes.length.toLocaleString('ru-RU')} из ${total.toLocaleString('ru-RU')}...`, severity: 'info' });
      page++;
    }

    return codes;
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * Экспорт кодов партии в файл (CSV/XLSX) на стороне браузера
   * @param {Object} batchRow - строка партии из таблицы
   * @param {string} format - формат файла ('csv' | 'xlsx')
   * @returns {Promise<void>}
   */
  const handleExport = async (batchRow, format) => {
    if (exporting || !selectedProduct || !batchRow) return;
    setExporting(format);

    try {
      const codes = await fetchAllBarcodes(batchRow);
      if (!codes.length) {
        setSnackbar({ open: true, message: 'В партии нет кодов', severity: 'warning' });
        return;
      }

      const date = new Date().toISOString().split('T')[0];
      const filename = `barcodes_${selectedProduct.product_code}_batch_${batchRow.batch}_${date}.${format}`;

      if (format === 'csv') {
        /** Формат по образцу barcodes.export.csv: разделитель ";", код в кавычках, дата в ISO */
        const rows = codes.map((item) => `"${item.barcode}";${new Date(item.created_at).toISOString()}`);
        const csv = `Код;"Время создания"\n${rows.join('\n')}`;
        downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), filename);
      } else {
        const rows = codes.map((item) => ({
          'Код': item.barcode,
          'Время создания': new Date(item.created_at).toISOString(),
        }));
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Баркоды');
        XLSX.writeFile(workbook, filename);
      }

      setSnackbar({ open: true, message: `Экспортировано ${codes.length.toLocaleString('ru-RU')} кодов в ${format.toUpperCase()}`, severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Ошибка экспорта: ${error.message}`, severity: 'error' });
    } finally {
      setExporting(null);
    }
  };

  /** --- Меню экспорта --- */

  const handleOpenExportMenu = (event, batchRow) => {
    setExportMenu({ anchor: event.currentTarget, batchRow });
  };

  const handleCloseExportMenu = () => {
    setExportMenu({ anchor: null, batchRow: null });
  };

  const handleBrowserExport = (format) => {
    const batchRow = exportMenu.batchRow;
    handleCloseExportMenu();
    handleExport(batchRow, format);
  };

  /**
   * Файл формируется и стримится модулем - подходит для очень больших партий
   * @param {string} format - формат файла ('csv' | 'xlsx')
   */
  const handleServerExport = (format) => {
    const batchRow = exportMenu.batchRow;
    handleCloseExportMenu();
    if (!batchRow || !selectedProduct) return;

    const link = document.createElement('a');
    link.href = barcodeApi.getExportUrl(selectedProduct.id, batchRow.batch, format);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSnackbar({ open: true, message: 'Файл формируется на сервере, загрузка начнётся автоматически', severity: 'info' });
  };

  /** --- Просмотр кодов партии --- */

  /**
   * Загрузка кодов партии для диалога просмотра (серверная пагинация)
   * @param {Object} batchRow - строка партии из таблицы
   * @param {number} page - номер страницы (с 0)
   * @param {number} rowsPerPage - размер страницы
   * @returns {Promise<void>}
   */
  const fetchViewCodes = async (batchRow, page, rowsPerPage) => {
    try {
      setViewLoading(true);
      const payload = await barcodeApi.getBatchBarcodes(selectedProduct.id, batchRow.batch, page + 1, rowsPerPage);
      setViewCodes(payload.barcodes?.data || []);
      setViewTotal(payload.count || 0);
    } catch (error) {
      setSnackbar({ open: true, message: `Не удалось загрузить коды: ${error.message}`, severity: 'error' });
    } finally {
      setViewLoading(false);
    }
  };

  const handleOpenViewDialog = (batchRow) => {
    setViewBatch(batchRow);
    setViewCodes([]);
    setViewTotal(0);
    setViewPage(0);
    setViewRowsPerPage(50);
    setViewDialogOpen(true);
    fetchViewCodes(batchRow, 0, 50);
  };

  const handleViewTableChange = (action, tableState) => {
    if (action === 'changePage') {
      setViewPage(tableState.page);
      fetchViewCodes(viewBatch, tableState.page, viewRowsPerPage);
    }
    if (action === 'changeRowsPerPage') {
      setViewRowsPerPage(tableState.rowsPerPage);
      setViewPage(0);
      fetchViewCodes(viewBatch, 0, tableState.rowsPerPage);
    }
  };

  /** --- Проверка кода --- */

  const handleOpenVerifyDialog = () => {
    setVerifyCode('');
    setVerifyResult(null);
    setVerifyDialogOpen(true);
  };

  /**
   * Тестовая проверка кода (отметка о проверке в базе не ставится)
   * @returns {Promise<void>}
   */
  const handleVerify = async () => {
    /** Нормализация: модуль ждёт 13 символов с пробелом после 6-й позиции */
    const digits = verifyCode.replace(/\s/g, '');
    if (!digits) return;
    const code = digits.length === 12 ? `${digits.slice(0, 6)} ${digits.slice(6)}` : verifyCode.trim();

    setVerifyLoading(true);
    setVerifyResult(null);
    try {
      const result = await barcodeApi.verifyBarcode(code);
      const doc = result.barcode;
      const product = doc ? products.find((item) => Number(item.id) === Number(doc.product_id)) : null;
      /** Год баркода - первые две цифры кода (например, 25 = 2025) */
      const year = digits.length === 12 ? 2000 + Number(digits.slice(0, 2)) : null;
      setVerifyResult({ ...result, product, batch: doc?.batch, year });
    } catch (error) {
      setVerifyResult({ error: error.message });
    } finally {
      setVerifyLoading(false);
    }
  };

  const verifySeverity = (status) => {
    if (status === 1) return 'success';
    if (status === 0) return 'warning';
    return 'error';
  };

  /** --- Таблица --- */

  const columns = [
    {
      name: "product_name",
      label: "Товар",
      options: {
        filter: true,
        sort: true,
      }
    },
    {
      name: "product_code",
      label: "Код товара",
      options: {
        filter: true,
        sort: true,
      }
    },
    {
      name: "year",
      label: "Год",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value ? 2000 + Number(value) : '',
      }
    },
    {
      name: "batch",
      label: "№ партии",
      options: {
        filter: true,
        sort: true,
      }
    },
    {
      name: "amount",
      label: "Количество кодов",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => Number(value).toLocaleString('ru-RU'),
      }
    },
  ];

  const data = batches.map((item) => ({
    product_name: selectedProduct?.product_name,
    product_code: selectedProduct?.product_code,
    year: item.year,
    batch: item.batch,
    amount: item.amount,
  }));

  const options = {
    storageKey: 'barcodeBatchesTable',
    jumpToPage: true,
    selectableRows: 'single',
    rowsPerPageOptions: [10, 25, 50, 100],
    filterType: 'multiselect',
    downloadOptions: {
      filename: `Партии баркодов.${new Date().toISOString().split('T')[0]}.csv`,
      filterOptions: {
        useDisplayedColumnsOnly: true,
        useDisplayedRowsOnly: true,
      }
    },
    textLabels: {
      body: {
        noMatch: loading ? (<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>) : (selectedProduct ? 'У товара нет партий баркодов' : 'Выберите товар для просмотра партий'),
        toolTip: "Сортировка",
        columnHeaderTooltip: (column) => `Сортировка для ${column.label}`
      },
      pagination: {
        next: "Следующая страница",
        previous: "Предыдущая страница",
        rowsPerPage: "Строк на странице:",
        displayRows: "из",
        jumpToPage: "Перейти на страницу:",
      },
      toolbar: {
        search: "Поиск",
        downloadCsv: "Скачать CSV",
        print: "Печать",
        viewColumns: "Просмотр столбцов",
        filterTable: "Фильтр таблицы",
      },
      filter: {
        all: "Все",
        title: "ФИЛЬТРЫ",
        reset: "СБРОС",
      },
      viewColumns: {
        title: "Показать столбцы",
        titleAria: "Показать/скрыть столбцы таблицы",
      },
      selectedRows: {
        text: "выбрано",
        delete: "Удалить",
        deleteAria: "Удалить выбранные строки",
      },
    },
    customToolbarSelect: (selectedRows, displayData, setSelectedRows) => (
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={{ marginRight: '0.5rem' }}>
          <Tooltip title={"Просмотр кодов"}>
            <IconButton
              onClick={() => {
                const selectedRow = data[selectedRows.data[0].dataIndex];
                setSelectedRows([]);
                handleOpenViewDialog(selectedRow);
              }}
            ><VisibilityIcon />
            </IconButton>
          </Tooltip>
        </div>
        <div style={{ marginRight: '0.5rem' }}>
          <Tooltip title={"Экспорт"}>
            <span>
              <IconButton
                disabled={Boolean(exporting)}
                onClick={(event) => {
                  const selectedRow = data[selectedRows.data[0].dataIndex];
                  setSelectedRows([]);
                  handleOpenExportMenu(event, selectedRow);
                }}
              >
                {exporting ? <CircularProgress size={24} /> : <FileDownloadIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </div>
        {canEdit && (
          <div style={{ marginRight: '0.5rem' }}>
            <Tooltip title={"Удалить партию"}>
              <IconButton
                onClick={() => {
                  const selectedRow = data[selectedRows.data[0].dataIndex];
                  setSelectedRows([]);
                  handleOpenDeleteDialog(selectedRow);
                }}
              ><DeleteIcon />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>)
  };

  /** --- Таблица просмотра кодов партии (серверная пагинация) --- */

  const viewColumns = [
    {
      name: "barcode",
      label: "Код",
      options: { filter: false, sort: false }
    },
    {
      name: "created_at",
      label: "Время создания",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => value ? new Date(value).toLocaleString('ru-RU') : '',
      }
    },
    {
      name: "was_searched",
      label: "Проверен",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <Chip
            label={value ? 'Да' : 'Нет'}
            color={value ? 'warning' : 'success'}
            size="small"
            variant="outlined"
          />)
      }
    },
  ];

  const viewOptions = {
    serverSide: true,
    jumpToPage: true,
    count: viewTotal,
    page: viewPage,
    rowsPerPage: viewRowsPerPage,
    rowsPerPageOptions: [50, 100, 500],
    onTableChange: handleViewTableChange,
    selectableRows: 'none',
    filter: false,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    elevation: 0,
    /** Фиксированная высота тела таблицы - скроллится только список кодов */
    tableBodyHeight: '55vh',
    textLabels: {
      body: {
        noMatch: viewLoading ? (<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>) : 'Коды не найдены',
        toolTip: "Сортировка",
      },
      pagination: {
        next: "Следующая страница",
        previous: "Предыдущая страница",
        rowsPerPage: "Строк на странице:",
        displayRows: "из",
        jumpToPage: "Перейти на страницу:",
      },
      toolbar: {
        search: "Поиск",
        downloadCsv: "Скачать CSV",
        print: "Печать",
        viewColumns: "Просмотр столбцов",
        filterTable: "Фильтр таблицы",
      },
    },
  };

  const appTheme = useTheme();
  const getMuiTheme = () => createTheme(appTheme, {
    components: {
      MUIDataTableToolbar: {
        styleOverrides: {
          filterPaper: {
            width: '25%',
            minWidth: '20vw',
          },
        },
      },
      MuiGrid: {
        styleOverrides: {
          root: {
            flexBasis: '100% !important',
            maxWidth: '100% !important',
            width: '100%',
            marginLeft: '0',
          },
          item: {
            paddingLeft: '0 !important',
            paddingTop: '1rem !important',
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '1rem',
            minWidth: '20vw !important',
            padding: '0.5rem !important',
          }
        }
      },
      MUIDataTableViewCol: {
        styleOverrides: {
          root: {
            padding: '1.5rem !important',
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: '0.5rem !important',
          }
        }
      },
    },
  });

  const progressPercent = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <>
      {/** Заголовок */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Подлинность товаров
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Управление партиями кодов подлинности и проверка кодов товаров
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<VerifiedUserIcon />}
            onClick={handleOpenVerifyDialog}
          >
            Проверить код
          </Button>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
              disabled={productsLoading}
            >
              Создать партию
            </Button>
          )}
        </Box>
      </Box>

      {/** Выбор товара */}
      <Autocomplete
        sx={{ mb: 3, maxWidth: 500 }}
        options={products}
        loading={productsLoading}
        value={selectedProduct}
        onChange={(event, newValue) => handleSelectProduct(newValue)}
        isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
        getOptionLabel={(option) => `${option.product_name} (код ${option.product_code})`}
        renderInput={(params) => (
          <TextField {...params} label="Товар" fullWidth />
        )}
      />

      {/** Таблица партий */}
      <ThemeProvider theme={getMuiTheme()}>
        <MUIDataTable
          title={selectedProduct ? `Партии баркодов: ${selectedProduct.product_name}` : 'Партии баркодов'}
          data={data}
          columns={columns}
          options={options}
        />
      </ThemeProvider>

      {/** Диалог создания партии */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Создать партию баркодов</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={products}
                value={createProduct}
                onChange={(event, newValue) => setCreateProduct(newValue)}
                isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                getOptionLabel={(option) => `${option.product_name} (код ${option.product_code})`}
                disabled={generating}
                renderInput={(params) => (
                  <TextField {...params} label="Товар *" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Количество кодов *"
                value={createCount}
                onChange={(e) => setCreateCount(e.target.value)}
                disabled={generating}
                inputProps={{ min: 1 }}
              />
            </Grid>
            {generating && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <LinearProgress variant="determinate" value={progressPercent} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {progress.done.toLocaleString('ru-RU')} / {progress.total.toLocaleString('ru-RU')}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Генерация выполняется на сервере, это может занять время
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>
            {generating ? 'Закрыть' : 'Отмена'}
          </Button>
          <Button
            onClick={handleGenerate}
            variant="contained"
            disabled={generating || !createProduct || !(parseInt(createCount, 10) > 0)}
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      {/** Диалог подтверждения удаления */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Удалить партию?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {batchToDelete && selectedProduct &&
              `Будет удалена партия №${batchToDelete.batch} товара «${selectedProduct.product_name}» - ${Number(batchToDelete.amount).toLocaleString('ru-RU')} кодов. Действие необратимо.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Удаление...' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/** Меню экспорта партии */}
      <Menu
        anchorEl={exportMenu.anchor}
        open={Boolean(exportMenu.anchor)}
        onClose={handleCloseExportMenu}
      >
        <MenuItem onClick={() => handleBrowserExport('csv')}>
          <ListItemIcon><FileDownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>CSV (в браузере)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleBrowserExport('xlsx')}>
          <ListItemIcon><GridOnIcon fontSize="small" /></ListItemIcon>
          <ListItemText>XLSX (в браузере)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleServerExport('csv')}>
          <ListItemIcon><FileDownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>CSV (с сервера - для больших партий)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleServerExport('xlsx')}>
          <ListItemIcon><GridOnIcon fontSize="small" /></ListItemIcon>
          <ListItemText>XLSX (с сервера - для больших партий)</ListItemText>
        </MenuItem>
      </Menu>

      {/** Диалог просмотра кодов партии */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>
          {viewBatch && selectedProduct &&
            `Коды партии №${viewBatch.batch} - ${selectedProduct.product_name} (${viewTotal.toLocaleString('ru-RU')} шт.)`}
        </DialogTitle>
        <DialogContent sx={{ overflow: 'hidden', px: 1, pt: 1 }}>
          <ThemeProvider theme={getMuiTheme()}>
            {/** Скролл только внутри области таблицы - без второго скроллбара у диалога */}
            <Box sx={{ '& .MuiTableContainer-root': { maxHeight: '55vh' } }}>
              <MUIDataTable
                title=""
                data={viewCodes}
                columns={viewColumns}
                options={viewOptions}
              />
            </Box>
          </ThemeProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {/** Диалог проверки кода */}
      <Dialog open={verifyDialogOpen} onClose={() => setVerifyDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Проверка кода</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Тестовая проверка - отметка о проверке в базе не ставится.
          </DialogContentText>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              label="Код"
              placeholder="259417 694947"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleVerify(); }}
              disabled={verifyLoading}
            />
            <Button
              variant="contained"
              onClick={handleVerify}
              disabled={verifyLoading || !verifyCode.trim()}
            >
              {verifyLoading ? <CircularProgress size={24} /> : 'Проверить'}
            </Button>
          </Box>
          {verifyResult && (
            <Box sx={{ mt: 2 }}>
              {verifyResult.error ? (
                <Alert severity="error">{verifyResult.error}</Alert>
              ) : (
                <>
                  <Alert severity={verifySeverity(verifyResult.status)}>
                    {verifyResult.statusMessage}
                  </Alert>
                  {verifyResult.year && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">Год баркода: {verifyResult.year}</Typography>
                    </Box>
                  )}
                  {verifyResult.product && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">Товар: {verifyResult.product.product_name}</Typography>
                      {verifyResult.batch != null && (
                        <Typography variant="body2">Партия: №{verifyResult.batch}</Typography>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialogOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {/** Уведомления */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminBarcodesPage;
